import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import z from 'zod';
import { useForm } from '../../../hooks/Form';
import { api } from '../../../utils/api';
import { RESET_PASSWORD_STEPS, usePasswordResetContext } from '../PasswordResetPage';

const schema = z.object({
  verificationCode: z.string()
    .nonempty('Verification code is required')
});

function createInitialValues() {
  return {
    verificationCode: ''
  };
}

function createInitialErrors() {
  return {
    verificationCode: null
  };
}

export default function PasswordResetStart() {
  const { setStep } = usePasswordResetContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const { isLoading, setIsLoading, error, setError, errors, setErrors, setFieldError, values, setFieldValue } = useForm({
    initialValues: createInitialValues(),
    initialErrors: createInitialErrors()
  });

  const handleVerificationCode = async () => {
    try {
      setError(null);
      setErrors(createInitialErrors());

      setIsLoading(true);

      const result = schema.safeParse(values);

      if (!result.success) {
        result.error.issues.forEach(err => {
          setFieldError(err.path[0], err.message);
        });

        return;
      }

      await api.put('/password_resets/verify', {
        email: decodeURIComponent(searchParams.get('em')),
        code: result.data.verificationCode
      });

      setSearchParams((oldSearchParams) => {
          oldSearchParams.set('cd', result.data.verificationCode);
          return oldSearchParams;
      });

      setStep(RESET_PASSWORD_STEPS.FINISH);

    } catch {
      setError('Invalid verification code.');

    } finally {
      setIsLoading(false);
    }
  };

  /** @param {React.FormEvent<HTMLFormElement>} event */
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    handleVerificationCode();
  };

  useEffect(() => {
    if (values.verificationCode.length === 6) {
      handleVerificationCode();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.verificationCode]);

  return (
    <Fragment>
      <form onSubmit={handleSubmit} className='form'>
        <h1 className='form__title'>
          Please check your email inbox to confirm your e-mail address
        </h1>
        <p className='form__subtitle'>
          We are going to send you an e-mail with a link to confirm your e-mail address
        </p>
        <div className='form__field-group'>
          <div className='text-field text-field--white form__field'>
            <label htmlFor='verification-code' className='text-field__label'>
              Verification code
            </label>
            <input
              id='verification-code'
              name='verification-code'
              type='text'
              placeholder='• • • • • •'
              value={values.verificationCode}
              maxLength={6}
              required
              onChange={(e) => setFieldValue('verificationCode', e.target.value)}
              className='text-field__input'
            />
            {errors.verificationCode && (
              <p className='text-field__error'>
                {errors.verificationCode}
              </p>
            )}
          </div>
        </div>
        {error && (
          <p className='error error--black'>
            {error}
          </p>
        )}
        <button type='submit' className='form__button--wide button button--white button--fill'>
          {isLoading ? 'Loading...' : 'Verify code'}
        </button>
      </form>
    </Fragment>
  );
}